import React from 'react';
import { Divider, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { translate } from '@fiverr-private/i18n-react';
import { MatchSellerLanguageLevel } from '../types';
import { Preference } from '../Preference';
import { Gql } from '../../../types';

const T_PREFIX = 'dynamic_matching.freelancer_preferences.fields.languages';

const getLanguageName = (code: string) => translate(`languages.codes.${code.toLocaleLowerCase()}`);
const getMinLevelText = (minLevel: string) => translate(`${T_PREFIX}.level.${minLevel}`);
const freelancerSpeaks = (sellerName: string) =>
  `${translate(`${T_PREFIX}.tooltip.profile`)} ${sellerName} ${translate(`${T_PREFIX}.tooltip.speaks`)}`;
const noLanguages = (sellerName: string) => `${sellerName} ${translate(`${T_PREFIX}.tooltip.no_languages`)}`;
const sellerSelfLanguages = () => translate(`${T_PREFIX}.tooltip.seller`);

export const LanguagePreference = ({
  languages,
  matchIndicator,
  sellerName,
  sellerLanguages,
}: {
  languages: Gql.DmBriefLanguagePreference[];
  matchIndicator?: Gql.Maybe<Gql.DmBriefMatchIndicator>;
  sellerName?: string;
  sellerLanguages?: MatchSellerLanguageLevel[];
}) => {
  const tooltipContent = <TooltipText sellerName={sellerName} sellerLanguages={sellerLanguages} />;
  return (
    <Preference name="languages" matchIndicator={matchIndicator} tooltipContent={tooltipContent}>
      {languages
        .filter((language) => language.languageCode !== Gql.DmBriefLanguageCode.Zz)
        .map((language, index) => (
          <Stack key={index} direction="row" display="inlineFlex" gap="1">
            <Typography color="grey_1200" as="p" size="b_md">
              {getLanguageLevelText(language.languageCode, language.minProficiency.toLowerCase())}
            </Typography>
            {index !== languages.length - 1 && (
              <Divider hidden={{ default: true, sm: false }} height="20px" marginX={'2'} direction="vertical" />
            )}
          </Stack>
        ))}
    </Preference>
  );
};

const getLanguageLevelText = (language: string, level: string) =>
  `${getLanguageName(language)}: ${getMinLevelText(level)}`;

const getTooltipPreText = (sellerName?: string, sellerLanguages?: MatchSellerLanguageLevel[]) => {
  if (sellerName) {
    if (sellerLanguages) {
      return freelancerSpeaks(sellerName);
    }
    return noLanguages(sellerName);
  }
  return sellerSelfLanguages();
};

const TooltipText = ({
  sellerName,
  sellerLanguages,
}: {
  sellerName?: string;
  sellerLanguages?: MatchSellerLanguageLevel[];
}) => (
  <>
    <Typography>{getTooltipPreText(sellerName, sellerLanguages)}</Typography>
    {sellerLanguages?.map((language, index) => (
      <Stack key={index} direction="column" display="inlineFlex">
        <Typography>{getLanguageLevelText(language.code, language.level.toLowerCase())}</Typography>
      </Stack>
    ))}
  </>
);
